import { zodResolver } from "@hookform/resolvers/zod";
import { CheckboxIndicator } from "@radix-ui/react-checkbox";
import { set } from "date-fns";
import { SubmitHandler, useForm } from "react-hook-form";

import { DateTimeInput } from "@/components/inputs/DateTimeInput";
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PGN_PROVIDER_DISPLAY_NAMES, handleProviderTypeChange } from "@/utils/pgn-provider";

import { Checkbox } from "../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { PGN_PROVIDER_TYPES, RoundInput, zRoundInput } from "./validation/round";

export const RoundForm = ({
    onSubmit,
    defaultValues,
    onDelete,
}: {
    onSubmit: SubmitHandler<RoundInput>;
    defaultValues: RoundInput;
    onDelete?: () => void;
}) => {
    const form = useForm<RoundInput>({
        defaultValues: defaultValues,
        mode: "onBlur",
        resolver: zodResolver(zRoundInput),
    });
    const roundSyncerConfigWatch = form.watch("roundSyncerConfig", undefined);

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Title</FormLabel>
                            <FormControl>
                                <Input placeholder="Round 1" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="scheduledStartAt"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Scheduled start at</FormLabel>
                            <FormControl>
                                <DateTimeInput value={field.value} onValue={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="lastGameScheduledStartAt"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Last game scheduled start at</FormLabel>
                            <FormControl>
                                <DateTimeInput
                                    value={field.value ?? null}
                                    onValue={field.onChange}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="evaluationSurgeProtectionOverride"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Round surge override</FormLabel>
                            <FormDescription>
                                Controls surge protection for this round to avoid overwhelming the
                                evaluation service during large rounds. Inherit uses the
                                tournament-level setting; Force ON enables surge protection for this
                                round; Force OFF disables it for this round.
                            </FormDescription>
                            <FormControl>
                                <Select
                                    onValueChange={val => {
                                        if (val === "inherit") {
                                            form.setValue(
                                                "evaluationSurgeProtectionOverride",
                                                null
                                            );
                                        } else if (val === "true") {
                                            form.setValue(
                                                "evaluationSurgeProtectionOverride",
                                                true
                                            );
                                        } else if (val === "false") {
                                            form.setValue(
                                                "evaluationSurgeProtectionOverride",
                                                false
                                            );
                                        }
                                    }}
                                    value={
                                        field.value === null || field.value === undefined
                                            ? "inherit"
                                            : field.value
                                              ? "true"
                                              : "false"
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Inherit from tournament" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            <SelectItem value="inherit">
                                                Inherit from tournament
                                            </SelectItem>
                                            <SelectItem value="true">Force ON</SelectItem>
                                            <SelectItem value="false">Force OFF</SelectItem>
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <div className="my-4 flex flex-col gap-2 rounded border bg-gray-50 p-4">
                    <FormField
                        control={form.control}
                        name="roundSyncerConfig"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Choose PGN provider type</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={value => {
                                            handleProviderTypeChange(form, value);
                                        }}
                                        defaultValue={field.value?.pgnProviderType ?? "MANUAL"}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Choose Syncer Config type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectGroup>
                                                {Object.entries(PGN_PROVIDER_DISPLAY_NAMES).map(
                                                    ([value, label]) => (
                                                        <SelectItem key={value} value={value}>
                                                            {label}
                                                        </SelectItem>
                                                    )
                                                )}
                                            </SelectGroup>
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    {typeof roundSyncerConfigWatch?.pgnProviderType === "string" &&
                    PGN_PROVIDER_TYPES.includes(
                        roundSyncerConfigWatch?.pgnProviderType as string
                    ) ? (
                        <div className="flex flex-col gap-2">
                            {roundSyncerConfigWatch?.pgnProviderType === "POLL_PGN" && (
                                <FormField
                                    control={form.control}
                                    name="roundSyncerConfig.pollPgnSource"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Poll PGN Source URL</FormLabel>
                                            <FormControl>
                                                <Input
                                                    className="flex-1"
                                                    placeholder="URL to Poll PGN source"
                                                    value={field.value ?? ""}
                                                    onChange={field.onChange}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}
                            {roundSyncerConfigWatch?.pgnProviderType === "STREAMING_PGN" && (
                                <FormField
                                    control={form.control}
                                    name="roundSyncerConfig.streamingPgnSource"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Streaming PGN Source URL</FormLabel>
                                            <FormControl>
                                                <Input
                                                    className="flex-1"
                                                    placeholder="URL to Streaming PGN source"
                                                    value={field.value ?? ""}
                                                    onChange={field.onChange}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}
                            {roundSyncerConfigWatch?.pgnProviderType ===
                                "LIVECHESS_CLOUD_ROUND" && (
                                <>
                                    <FormField
                                        control={form.control}
                                        name="roundSyncerConfig.livechesscloudRoundId"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Livechess Cloud Round ID</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        className="flex-1"
                                                        placeholder="Round ID from Livechess Cloud"
                                                        value={field.value ?? ""}
                                                        onChange={field.onChange}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="roundSyncerConfig.livechesscloudRoundNumber"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Livechess Cloud Round Number</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        className="flex-1"
                                                        type="number"
                                                        min="1"
                                                        placeholder="Round number"
                                                        value={field.value ?? ""}
                                                        onChange={e =>
                                                            field.onChange(
                                                                parseInt(e.target.value) || 1
                                                            )
                                                        }
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </>
                            )}
                            {roundSyncerConfigWatch?.pgnProviderType !== "MANUAL" ? (
                                <>
                                    <FormField
                                        control={form.control}
                                        name="roundSyncerConfig.pollStart"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Poll start</FormLabel>
                                                <FormControl>
                                                    <DateTimeInput
                                                        value={
                                                            field.value ??
                                                            set(new Date(), {
                                                                hours: 12,
                                                                minutes: 0,
                                                                seconds: 0,
                                                                milliseconds: 0,
                                                            })
                                                        }
                                                        onValue={field.onChange}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="roundSyncerConfig.pollEnd"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Poll end</FormLabel>
                                                <FormControl>
                                                    <DateTimeInput
                                                        value={
                                                            field.value ??
                                                            set(new Date(), {
                                                                hours: 12,
                                                                minutes: 0,
                                                                seconds: 0,
                                                                milliseconds: 0,
                                                            })
                                                        }
                                                        onValue={field.onChange}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="roundSyncerConfig.pollCreateGames"
                                        render={({ field }) => (
                                            <FormItem className="flex items-start gap-2">
                                                <FormLabel>Create games from PGN?</FormLabel>
                                                <FormControl>
                                                    <Checkbox
                                                        checked={field.value ?? false}
                                                        onClick={() => {
                                                            field.onChange(!field.value);
                                                        }}
                                                    >
                                                        <CheckboxIndicator />
                                                    </Checkbox>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="roundSyncerConfig.pgnDelaySecs"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>PGN Delay (seconds)</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        placeholder="0"
                                                        value={field.value ?? 0}
                                                        onChange={e =>
                                                            field.onChange(
                                                                parseInt(e.target.value) || 0
                                                            )
                                                        }
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </>
                            ) : null}
                        </div>
                    ) : null}
                </div>

                <div className="flex justify-between gap-2">
                    <Button
                        type="submit"
                        disabled={!form.formState.isValid || form.formState.isSubmitting}
                    >
                        Save
                    </Button>

                    {onDelete && (
                        <Button
                            type="button"
                            variant="outline"
                            className="border-red-600 text-red-600"
                            onClick={onDelete}
                        >
                            Delete round
                        </Button>
                    )}
                </div>
            </form>
        </Form>
    );
};
